import React, { useState, useEffect, useMemo } from 'react';
import { Toaster } from 'react-hot-toast';
import { DashboardHeader } from './DashboardHeader';
import { Sidebar } from './Sidebar';
import { PermissionChangeListener } from './PermissionChangeListener';
import { useUnreadMessages } from '../../hooks/useUnreadMessages';
import { UserProfileDrawer } from '../ui/UserProfileDrawer';
import { useAuthStore } from '../../store/auth.store';
import { useProductDrawerStore } from '../../store/productDrawer.store';
import { CreateProductDrawer } from '../products/CreateProductDrawer';
import { useQueryClient } from '@tanstack/react-query';

interface Usuario {
  id: string;
  email: string;
  nombreCompleto: string;
  tipoUsuario: string;
  estado: boolean;
  roles: any[];
}

interface DashboardLayoutProps {
  children: React.ReactNode;
}

export const DashboardLayout: React.FC<DashboardLayoutProps> = ({ children }) => {
  const [usuario, setUsuario] = useState<Usuario | null>(null);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isProfileDrawerOpen, setIsProfileDrawerOpen] = useState(false);
  const { setUser } = useAuthStore();
  const queryClient = useQueryClient();
  const { isOpen, isMinimized, productToEdit, minimize, maximize, close } = useProductDrawerStore();

  // Iniciar polling global de mensajes no leídos
  useUnreadMessages();

  useEffect(() => {
    // Obtener datos del usuario desde localStorage
    const userData = localStorage.getItem('userData');
    if (userData && userData !== 'undefined') {
      try {
        const parsedUser = JSON.parse(userData);
        if (parsedUser && typeof parsedUser === 'object') {
          setUsuario(parsedUser);
          // Cargar usuario en el store de Zustand
          setUser(parsedUser);
        } else {
          console.warn('Invalid user data in localStorage:', parsedUser);
          // Limpiar localStorage si los datos están corruptos
          localStorage.removeItem('userData');
        }
      } catch (error) {
        console.error('Error parsing user data:', error);
        // Limpiar localStorage si hay error de parsing
        localStorage.removeItem('userData');
      }
    }
  }, [setUser]);

  // Memoizar el usuario para evitar re-renders innecesarios
  const memoizedUsuario = useMemo(() => usuario, [usuario?.id, usuario?.email]);

  return (
    <div className="min-h-screen bg-gray-50">
      <Toaster 
        position="top-right"
        toastOptions={{
          duration: 3000,
          style: {
            background: '#363636',
            color: '#fff',
          },
          success: {
            duration: 3000,
            iconTheme: {
              primary: '#10B981',
              secondary: '#fff',
            },
          },
          error: {
            duration: 4000,
            iconTheme: {
              primary: '#EF4444',
              secondary: '#fff',
            },
          },
        }}
      />
      {/* Header fijo en la parte superior */}
      <DashboardHeader 
        usuario={memoizedUsuario || undefined} 
        onOpenProfile={() => setIsProfileDrawerOpen(true)}
      />
      
      <div className="flex pt-20"> {/* pt-20 para dar espacio al header fijo */}
        {/* Sidebar */}
        <Sidebar 
          isCollapsed={isCollapsed} 
          setIsCollapsed={setIsCollapsed}
        />
        
        {/* Main content area */}
        <main className="flex-1 overflow-y-auto" style={{ height: 'calc(100vh - 5rem)', maxHeight: 'calc(100vh - 5rem)' }}>
          {children}
        </main>
      </div>

      {/* Listener global para cambios de permisos */}
      <PermissionChangeListener />

      {/* Drawer de perfil de usuario */}
      <UserProfileDrawer 
        isOpen={isProfileDrawerOpen}
        onClose={() => setIsProfileDrawerOpen(false)}
      />

      {/* Drawer global de creación/edición de productos */}
      <CreateProductDrawer
        isOpen={isOpen}
        isMinimized={isMinimized}
        onMinimize={minimize}
        onMaximize={maximize}
        onClose={close}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ['products'] });
          queryClient.invalidateQueries({ queryKey: ['productos'] });
          queryClient.invalidateQueries({ queryKey: ['admin-products'] });
        }}
        productToEdit={productToEdit}
      />
    </div>
  );
};